import { useEffect, useRef, useState } from 'react';
import { useInView, motion, useAnimate } from 'motion/react';
import { TESTIMONIALS, MEMBER_COUNT_BASE } from '../../constants';

const STORAGE_KEY = 'catch_member_count';

/**
 * Get stored count from localStorage, or return base if not set
 */
function getStoredCount(): number {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      // Only use stored value if it's greater than base (never go backwards)
      if (!isNaN(parsed) && parsed >= MEMBER_COUNT_BASE) {
        return parsed;
      }
    }
  } catch {
    // localStorage not available
  }
  return MEMBER_COUNT_BASE;
}

/**
 * Save count to localStorage
 */
function saveCount(count: number): void {
  try {
    localStorage.setItem(STORAGE_KEY, count.toString());
  } catch {
    // localStorage not available
  }
}

/**
 * Animated counter that counts up on first view, then slowly increments
 * Persists to localStorage so it doesn't reset on refresh
 */
function AnimatedCounter() {
  const ref = useRef<HTMLSpanElement>(null);
  const [scope, animate] = useAnimate();
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const [count, setCount] = useState(() => getStoredCount());
  const [hasAnimated, setHasAnimated] = useState(false);
  const [displayCount, setDisplayCount] = useState(0);

  // Initial count-up animation when in view
  useEffect(() => {
    if (!isInView || hasAnimated) return;

    const target = count;
    const duration = 2000; // 2 seconds
    const startTime = performance.now();

    const animateCount = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease-out cubic for smooth deceleration
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentCount = Math.floor(easeOut * target);

      setDisplayCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animateCount);
      } else {
        setHasAnimated(true);
      }
    };

    requestAnimationFrame(animateCount);
  }, [isInView, hasAnimated, count]);

  // Slow drip increment after animation completes
  useEffect(() => {
    if (!hasAnimated) return;

    const increment = () => {
      setCount((prev) => {
        const newCount = prev + 1;
        saveCount(newCount);
        return newCount;
      });
      setDisplayCount((prev) => prev + 1);

      // Pop + green flash animation on increment
      animate(
        scope.current,
        {
          scale: [1, 1.15, 1],
          color: ['#64748b', '#22c55e', '#64748b'], // slate-500 → green-500 → slate-500
        },
        { duration: 0.5 }
      );
    };

    // Random interval between 10-30 seconds
    const getRandomInterval = () => Math.floor(Math.random() * 20000) + 10000;

    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNext = () => {
      timeoutId = setTimeout(() => {
        increment();
        scheduleNext();
      }, getRandomInterval());
    };

    scheduleNext();

    return () => clearTimeout(timeoutId);
  }, [hasAnimated, animate, scope]);

  return (
    <span ref={ref}>
      <motion.span ref={scope} style={{ display: 'inline-block' }}>
        {displayCount.toLocaleString()}
      </motion.span>
    </span>
  );
}

const StarIcon = () => (
  <svg className="w-4 h-4 text-catch-avocado-400" viewBox="0 0 20 20" fill="currentColor">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const TestimonialCard = ({
  quote,
  name,
  title,
  avatarUrl,
}: {
  quote: string;
  name: string;
  title: string;
  avatarUrl: string;
}) => (
  <div className="bg-white rounded-xl border-1 border-solid border-[#E3DCCE] p-5 flex flex-col justify-between h-full">
    <p className="text-sm text-slate-500 leading-relaxed mb-4">{quote}</p>
    <div className="flex items-center gap-3 mt-auto">
      <div className="w-10 h-10 rounded-full bg-slate-200 overflow-hidden flex-shrink-0">
        <img
          src={avatarUrl}
          alt={name}
          className="w-full h-full object-cover"
          onError={(e) => {
            // Fallback to initials if image fails to load
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent) {
              parent.innerHTML = `<span class="w-full h-full flex items-center justify-center text-sm font-medium text-slate-500">${name.charAt(0)}</span>`;
            }
          }}
        />
      </div>
      <div className="flex flex-col">
        <span className="text-sm font-semibold text-catch-dark-blue-90">{name}</span>
        <span className="text-xs text-slate-500">{title}</span>
      </div>
    </div>
  </div>
);

export const TestimonialsSection = () => {
  return (
    <div className="w-full">
      {/* Star rating and member count */}
      <div className="flex items-center justify-center gap-2 mb-4">
        <div className="flex gap-0.5">
          {[...Array(5)].map((_, i) => (
            <StarIcon key={i} />
          ))}
        </div>
        <span className="text-sm text-slate-600">
          Trusted by <AnimatedCounter /> members
        </span>
      </div>

      {/* Testimonial cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {TESTIMONIALS.map((testimonial) => (
          <TestimonialCard
            key={testimonial.id}
            quote={testimonial.quote}
            name={testimonial.name}
            title={testimonial.title}
            avatarUrl={testimonial.avatarUrl}
          />
        ))}
      </div>
    </div>
  );
};

export default TestimonialsSection;
